import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { LessonDelete } from "@shared/models/analytics/lesson"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { Course, CourseCalendarDateCustom, CourseCalendarTime } from "@shared/models/course"
import type { PatchModel } from "@shared/models/patch"
import Patch from "@shared/models/patch-updater"
import type { PreparedAction } from "../../action-doc-builder"
import _, { partial } from "lodash"
import moment from "moment"
import type { Narrative } from "../create-narrative"
import defaultLessonActivity from "./default-lesson-activity"
import defaultLessonContext from "./default-lesson-context"
import defaultLessonModels from "./default-lesson-models"

interface Payload {
  dateString: string
  course: Course
  cardStackId: string
  time: CourseCalendarTime | null
}

export function prepareLessonErase(payload: Payload): PreparedAction {
  return {
    name: LESSON_ERASE.name,
    payload: payload,
    patches: LESSON_ERASE.patches(payload),
    undoPatches: LESSON_ERASE.undoPatches(payload),
    narrative: partial(LESSON_ERASE.narrative, payload),
  }
}

export const LESSON_ERASE = {
  name: "LESSON_ERASE",

  patches(payload: Payload): Array<PatchModel> {
    let courseDate = _.chain(payload.course.attributes.calendar.dates)
      .filter((date) => date.type === "course-date-custom")
      .find((date: CourseCalendarDateCustom) => date.attributes.dateString === payload.dateString)
      .value()
    if (courseDate === undefined) throw Error("CourseDate is Undefined")
    return [
      Patch.create("course", payload.course.id, "attributes.calendar.dates", {
        id: courseDate.id,
      })
        .set("embedded", "attributes.cardStackId", null)
        .set("embedded", "attributes.time", null)
        .inc("attributes.calendar.version", 1).patch,
    ]
  },

  undoPatches(payload: Payload): Array<PatchModel> {
    let courseDate = _.chain(payload.course.attributes.calendar.dates)
      .filter((date) => date.type === "course-date-custom")
      .find((date: CourseCalendarDateCustom) => date.attributes.dateString === payload.dateString)
      .value()
    if (courseDate === undefined) throw Error("CourseDate is Undefined")
    return [
      Patch.create("course", payload.course.id, "attributes.calendar.dates", {
        id: courseDate.id,
      })
        .set("embedded", "attributes.cardStackId", payload.cardStackId)
        .set("embedded", "attributes.time", payload.time)
        .inc("attributes.calendar.version", 1).patch,
    ]
  },

  narrative(payload: Payload, findFn: FindFn, userId: string): Narrative | null {
    let models = defaultLessonModels(payload, findFn, userId)
    let courseTitle = payload.course.attributes.title
    let lessonTitle = models.cardStack ? models.cardStack.attributes.title : null
    let formattedDateString = moment(payload.dateString).format("ddd. MMM. D, YYYY")
    let newEvent: LessonDelete = {
      ...createBlankEvent(),
      root_id: models.planbook?.id ?? null,
      root_type: "planbook",
      primary_object_id: payload.cardStackId,
      date_plan_scheduled_for: models.dateStringForCardStack,
      primary_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
      primary_verb: "delete",
      primary_possessive: getPossessiveForPlanbook(findFn, userId, models.planbook?.id),
      primary_object_type: "lesson",
      primary_object_specifier: null,
      adverbial_verb: "removing",
      adverbial_object_type: "lesson",
      adverbial_object_specifier: "content",
      adverbial_object_id: null,
      adverbial_object_id_and_ancestor_ids: null,
    }

    return {
      title: "Erased A Lesson",
      titleForAnalytics: "Lesson - Erase",
      description: `Completely erased and deleted the history for "${lessonTitle}", your ${courseTitle} lesson on ${formattedDateString}`,
      context: defaultLessonContext(payload, models),
      activity: defaultLessonActivity(payload, models, "deleted"),
      newAnalyticsEvents: [newEvent],
    }
  },
}
export default LESSON_ERASE
